"use client";
/**
 * useOspLogin 兼容层 — 提供与 @osp/osp-web3-login useOspLogin 相同的接口
 *
 * 内部委托给新 auth 模块的 useAuth()，映射返回值
 * 消费方无需修改调用方式，仅改 import 路径即可
 */
import { useCallback } from "react";
import { useSnapshot } from "valtio";
import { useAuth } from "../hooks/useAuth";
import { authStore } from "../store/auth-store";
import type { Web3LoginSupportedType } from "./sdk-types";
import type { AuthProvider } from "../types";

type LoginTypeArg = Web3LoginSupportedType | "OSP_EMAIL";

const PROVIDER_MAP: Record<LoginTypeArg, AuthProvider> = {
  GOOGLE: "google",
  APPLE: "apple",
  TWITTER: "twitter",
  GOOGLE_ONE_TAP: "google",
  OSP_EMAIL: "email",
};

interface OspLoginOptions {
  email?: string;
  code?: string;
}

export function useOspLogin() {
  const { isAuthenticated, isLoading, login: authLogin, logout } = useAuth();
  const snap = useSnapshot(authStore);

  const error = snap.status.state === "error" ? snap.status.error : null;

  const login = useCallback(
    async (type: LoginTypeArg, options?: OspLoginOptions) => {
      const provider = PROVIDER_MAP[type];
      if (!provider) {
        return;
      }
      await authLogin(provider, {
        forceSelect: type !== "OSP_EMAIL",
        email: options?.email,
        code: options?.code,
      });
    },
    [authLogin],
  );

  const socialLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  return {
    login,
    socialLogin: login,
    socialLogout,
    logout: socialLogout,
    isLoading,
    isWeb3Fetching: isLoading,
    isWeb3Connected: isAuthenticated,
    isOspLogin: isAuthenticated,
    isOspEmailLogin: false,
    error,
    socialError: error,
    web3Error: error,
    currentLoginType:
      snap.status.state === "authenticating"
        ? (snap.status.provider?.toUpperCase() as
            | Web3LoginSupportedType
            | undefined)
        : undefined,
  };
}
